import {defineStore} from 'pinia';
import Network from "~/utils/network";
import i18n, {i18nInstance} from '~~/plugins/i18n';

const urls = {
    myDeals: '/bnpl/my-deal',
    myBusinessDeals: '/bnpl/my-deal/business',
    upcomingTransactions: '/bnpl/transaction/upcoming',
    upcomingBusinessTransactions: '/bnpl/transaction/business/upcoming',
    getMyCompanies: `/administration/company/my-companies`,
    getMyEmails: `/customer/business_email/my_business_emails`,
};

export const useSelfcareStore = defineStore('selfcare', {
    state: () => ({
        financier: '',
        loaded: false,
        breadcrumb: {
            items: [],
        },
        activePage: "dashboard",
        previousPage: 'dashboard',
        activePurchase: null,
        activeBusinessPurchase: null,
        activeTransaction: null,
        activeBusinessTransaction: null,
        activeTodo: null,
        deals: [],
        businessDeals: [],
        activePays: [],
        activeBusinessPays: [],
        pages: {
            "dashboard": {
                name: "bnpl:selfcare.menu.dashboard.name",
                desc: "bnpl:selfcare.menu.dashboard.description",
                icon: "gear",
                showInMenu: true,
                hasTokens: true,
            },
            "purchases": {
                name: "bnpl:selfcare.menu.purchases.name",
                desc: "bnpl:selfcare.menu.purchases.description",
                icon: "basket-shopping",
                showInMenu: true,
                hasTokens: true,
            },
            "b2b-purchases": {
                name: "bnpl:selfcare.menu.b2b-purchases.name",
                desc: "bnpl:selfcare.menu.b2b-purchases.description",
                icon: "basket-shopping",
                showInMenu: true,
                hasTokens: true,
            },
            "todos": {
                name: "bnpl:selfcare.menu.todos.name",
                desc: "bnpl:selfcare.menu.todos.description",
                icon: "clipboard-list",
                showInMenu: true,
                hasTokens: true,
            },
            "profile-settings": {
                name: "bnpl:selfcare.menu.profile-settings.name",
                desc: "bnpl:selfcare.menu.profile-settings.description",
                icon: "user",
                showInMenu: true,
                hasTokens: true,
            },
            "help": {
                name: "bnpl:selfcare.menu.help.name",
                desc: "bnpl:selfcare.menu.help.description",
                icon: "question",
                showInMenu: true,
                hasTokens: true,
            },
            "payment": {
                name: "bnpl:selfcare.menu.payment.name",
                showInMenu: false,
                hasTokens: true,
            },
            "b2b-payment": {
                name: "bnpl:selfcare.menu.b2b-payment.name",
                showInMenu: false,
                hasTokens: true,
            },
            "limit": {
                name: "bnpl:selfcare.menu.limit.name",
                desc: "bnpl:selfcare.menu.limit.description",
                icon: "clock",
                showInMenu: true,
                hasTokens: true,
            },
            "customer-limit": {
                name: "bnpl:selfcare.menu.customer-limit.name",
                desc: "bnpl:selfcare.menu.customer-limit.description",
                icon: "clock",
                showInMenu: true,
                hasTokens: true,
            },
            "rewards": {
                name: "bnpl:selfcare.menu.rewards.name",
                desc: "bnpl:selfcare.menu.rewards.description",
                icon: "gift",
                showInMenu: true,
                hasTokens: true,
            },
            "coupon": {
                name: "bnpl:selfcare.menu.coupon.name",
                desc: "bnpl:selfcare.menu.coupon.description",
                icon: "ticket-simple",
                showInMenu: true,
                hasTokens: true,
            },
        },
        isPaymentPartial: false,
        companies: {},
        companyEmails: {},
        dealsLoaded: false,
        b2bDealsLoaded: false,
        activePaysLoaded: false,
        activeBusinessPaysLoaded: false,
        companiesLoaded: false,
    }),
    actions: {
        navigateTo(pageId, deleteActives = true) {
            this.previousPage = this.activePage;
            if (deleteActives) {
                this.activePurchase = null;
                this.activeBusinessPurchase = null;
                this.activeTransaction = null;
                this.activeBusinessTransaction = null;
                this.activeTodo = null;
            }
            this.activePage = pageId;
            if (this.pages[pageId]) {
                this.breadcrumb.items = [i18nInstance.global.t(this.pages[pageId].name)];
            }
        },
        openTodo(todo) {
            this.activeTodo = todo;
            this.breadcrumb.items.push("Teendő");
        },
        openPurchase(deal) {
            this.activePurchase = deal;
            this.breadcrumb.items.push(i18nInstance.global.t("bnpl:breadcrumb.purchase") + " " + i18nInstance.global.d(deal.created, "numeric"));
        },
        openBusinessPurchase(deal) {
            this.activeBusinessPurchase = deal;
            this.breadcrumb.items.push(i18nInstance.global.t("bnpl:breadcrumb.purchase") + " " + i18nInstance.global.d(deal.created, "numeric"));
        },
        async loadEverything(force = false) {
            try {
                await this.fetchDeals(force);
                await this.fetchActivePays(force);
                if (this.activePurchase) {
                    this.activePurchase = this.deals.find(d => d.id === this.activePurchase.id);
                }
                await this.fetchB2bDeals(force);
                await this.fetchActiveBusinessPays(force);
                if (this.activeBusinessPurchase) {
                    this.activeBusinessPurchase = this.businessDeals.find(d => d.id === this.activeBusinessPurchase.id);
                }
            } finally {
                this.loaded = true;
            }
        },

        async fetchDeals(forceReload = false) {
            if (this.dealsLoaded && !forceReload) return;
            let allDeal = await Network.http(urls.myDeals);
            this.deals = allDeal
                .filter(d => d.applicationStatus !== "INITIAL")
                .sort((d1, d2) => new Date(d2.created) - new Date(d1.created));
            this.dealsLoaded = true;
        },

        async fetchB2bDeals(forceReload = false) {
            if (this.b2bDealsLoaded && !forceReload) return;
            let allBusinessDeals = await Network.http(urls.myBusinessDeals);
            this.businessDeals = allBusinessDeals
                .filter(d => d.applicationStatus !== "INITIAL")
                .sort((d1, d2) => new Date(d2.created) - new Date(d1.created));
            this.b2bDealsLoaded = true;
        },

        async fetchActivePays(forceReload = false) {
            if (this.activePaysLoaded && !forceReload) return;
            let activePays = await Network.http(urls.upcomingTransactions);
            this.activePays = activePays.sort((d1, d2) => new Date(d2.created) - new Date(d1.created));
            this.activePaysLoaded = true;
        },

        async fetchActiveBusinessPays(forceReload = false) {
            if (this.activeBusinessPaysLoaded && !forceReload) return;
            let activeBusinessPays = await Network.http(urls.upcomingBusinessTransactions);
            this.activeBusinessPays = activeBusinessPays.sort((d1, d2) => new Date(d2.created) - new Date(d1.created));
            this.activeBusinessPaysLoaded = true;
        },

        async fetchMyCompanies(forceReload = false) {
            if (this.companiesLoaded && !forceReload) return;
            this.companies = await Network.http(urls.getMyCompanies);
            this.companiesLoaded = true;
        },

        async fetchMyEmails() {
            this.companyEmails = await Network.http(urls.getMyEmails);
        }
    }
});
